<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { toast } from 'dashboard/helper/toast';
import {
  AVAILABLE_CUSTOM_ROLE_PERMISSIONS,
  MANAGE_ALL_CONVERSATION_PERMISSIONS,
  CONVERSATION_TEAM_PERMISSIONS,
  CONVERSATION_UNASSIGNED_PERMISSIONS,
  CONVERSATION_TEAM_UNASSIGNED_PERMISSIONS,
  CONVERSATION_PARTICIPATING_PERMISSIONS,
} from 'dashboard/constants/permissions.js';

import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import UnderlineTabs from 'dashboard/components-next/tabbar/UnderlineTabs.vue';
import KanbanRolePermissions from 'dashboard/components-next/kanban/KanbanRolePermissions.vue';
import StudioIntegrationToolsAPI from 'dashboard/api/studio/integrationTools';

const props = defineProps({
  mode: {
    type: String,
    default: 'add',
    validator: value => ['add', 'edit'].includes(value),
  },
  selectedRole: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close']);

const store = useStore();
const { t, te } = useI18n();

// Kanban, Vision e as duas telefonias são recursos premium ligados por conta no
// super admin. Conta sem o recurso não vê a aba (Kanban, Vision) nem a
// permissão (telefonia): configurar acesso a algo que a conta não tem só
// confunde o admin (conta 24 mostrava tudo). O que já estava salvo no papel NÃO
// é apagado — segue em `permissions`/`vision_tools` e volta a valer se o
// recurso for ligado. O backend já recusa as rotas com o recurso desligado
// (KanbanFeatureGated, VisionController, WhatsappSip::BaseController), então
// esconder aqui é só a tela acompanhando.
const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const isKanbanEnabled = computed(() =>
  isFeatureEnabledonAccount.value(currentAccountId.value, FEATURE_FLAGS.KANBAN)
);
const isVisionEnabled = computed(() =>
  isFeatureEnabledonAccount.value(currentAccountId.value, FEATURE_FLAGS.VISION)
);

// Permissão da lista que depende de um recurso da conta. Mesma régua do menu
// lateral: o Painel/Pausas/Histórico da Telefonia só existe com
// asterisk_telephony, e o WhatsApp Voice só com whatsapp_sip_telephony.
const PERMISSION_FEATURE = {
  telephony_reports_access: FEATURE_FLAGS.ASTERISK_TELEPHONY,
  whatsapp_voice_reports_access: FEATURE_FLAGS.WHATSAPP_SIP_TELEPHONY,
};
const isPermissionAvailable = permission => {
  const flag = PERMISSION_FEATURE[permission];
  return !flag || isFeatureEnabledonAccount.value(currentAccountId.value, flag);
};

const name = ref('');
const description = ref('');
const selectedPermissions = ref([]);
const isInitialLoad = ref(false);

// Duas decisões diferentes moram neste modal: O QUE o papel gerencia
// (permissões) e QUAIS tools ele usa no Vision. Em coluna única viravam uma
// escada de 32 checkboxes; em abas, cada decisão cabe numa tela e o rodapé
// (Cancelar/Salvar) nunca sai de vista.
const PERMISSIONS_TAB = 'permissions';
const KANBAN_TAB = 'kanban';
const VISION_TAB = 'vision';
const activeTab = ref(PERMISSIONS_TAB);

// Kanban tem aba própria (KanbanRolePermissions): acesso em escada, quais
// cards e o Analytics, cada pergunta com uma resposta. Continuam salvas em
// `permissions`, como as outras — esta lista só as tira da aba Permissões.
const KANBAN_PERMISSIONS = [
  'kanban_access',
  'kanban_deal_manage',
  'kanban_manage',
  'kanban_analytics_block',
  'kanban_own_deals_only',
];

// Agrupamento só VISUAL das permissões. A lista que vale continua sendo
// AVAILABLE_CUSTOM_ROLE_PERMISSIONS — por isso o bloco "Outras" existe: uma
// permissão nova que ninguém agrupou aparece nele em vez de sumir da tela sem
// ninguém notar.
const PERMISSION_GROUPS = [
  {
    key: 'conversations',
    names: [
      'conversation_manage',
      'conversation_team_manage',
      'conversation_unassigned_manage',
      'conversation_team_unassigned_manage',
      'conversation_participating_manage',
    ],
  },
  {
    key: 'contactsReports',
    names: [
      'contact_manage',
      'contact_edit',
      'report_manage',
      'knowledge_base_manage',
    ],
  },
  {
    key: 'access',
    names: [
      'inbox_access',
      'settings_access',
      'agents_access',
      'chamados_view',
      'massiva_access',
    ],
  },
  {
    key: 'telephony',
    names: ['telephony_reports_access', 'whatsapp_voice_reports_access'],
  },
  {
    key: 'restrictions',
    names: ['hide_resolved_conversations', 'conversation_create_block'],
  },
];

// Assistente Vision. `vision_tools` do papel é SEMPRE uma lista: as tools que
// quem tem o papel pode usar. Vazia = Vision desligado pra ele (avisado na
// tela). Admin não passa por aqui — tem tudo, por regra de produto.
const selectedVisionTools = ref([]);
const visionTools = ref([]);
const visionToolsLoading = ref(false);
const visionToolsError = ref(false);

const nameInput = ref(null);

const addCustomRole = reactive({
  showLoading: false,
  message: '',
});

const rules = computed(() => ({
  name: { required, minLength: minLength(2) },
  description: { required },
  selectedPermissions: { required, minLength: minLength(1) },
}));

const v$ = useVuelidate(rules, { name, description, selectedPermissions });

const resetForm = () => {
  name.value = '';
  description.value = '';
  selectedPermissions.value = [];
  selectedVisionTools.value = [];
  v$.value.$reset();
};

const populateEditForm = () => {
  isInitialLoad.value = true;
  name.value = props.selectedRole.name || '';
  description.value = props.selectedRole.description || '';
  selectedPermissions.value = props.selectedRole.permissions || [];
  // null/ausente (papel antigo, ou campo que a API não mandou) é o mesmo que
  // lista vazia: nenhuma tool liberada.
  const savedVisionTools = props.selectedRole.vision_tools;
  selectedVisionTools.value = Array.isArray(savedVisionTools)
    ? [...savedVisionTools]
    : [];

  // Permitir que o watch funcione após o carregamento inicial
  setTimeout(() => {
    isInitialLoad.value = false;
  }, 0);
};

watch(
  selectedPermissions,
  (newValue, oldValue) => {
    // Ignorar durante o carregamento inicial do formulário de edição
    if (isInitialLoad.value) return;

    // Check if manage all conversation permission is added or removed
    const hasAddedManageAllConversation =
      newValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS) &&
      !oldValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS);
    const hasRemovedManageAllConversation =
      oldValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS) &&
      !newValue.includes(MANAGE_ALL_CONVERSATION_PERMISSIONS);

    if (hasAddedManageAllConversation) {
      // If manage all conversation permission is added,
      // then add team, unassigned and participating permissions automatically
      selectedPermissions.value = [
        ...new Set([
          ...selectedPermissions.value,
          CONVERSATION_TEAM_PERMISSIONS,
          CONVERSATION_UNASSIGNED_PERMISSIONS,
          CONVERSATION_TEAM_UNASSIGNED_PERMISSIONS,
          CONVERSATION_PARTICIPATING_PERMISSIONS,
        ]),
      ];
    } else if (hasRemovedManageAllConversation) {
      // If manage all conversation permission is removed,
      // then only remove manage all conversation permission
      selectedPermissions.value = selectedPermissions.value.filter(
        p => p !== MANAGE_ALL_CONVERSATION_PERMISSIONS
      );
    }
  },
  { deep: true }
);

// O Switch decide um booleano por linha, mas o que o papel salva continua
// sendo a LISTA (selectedPermissions / selectedVisionTools). Cada linha deriva
// o estado de `includes` e o toggle devolve uma lista NOVA — reatribuir em vez
// de mutar é o que mantém o watch das permissões de conversa enxergando
// oldValue diferente de newValue.
const toggleInList = (listRef, value, checked) => {
  if (checked) {
    if (listRef.value.includes(value)) return;
    listRef.value = [...listRef.value, value];
  } else {
    listRef.value = listRef.value.filter(item => item !== value);
  }
};

const togglePermission = (permission, checked) =>
  toggleInList(selectedPermissions, permission, checked);

const toggleVisionTool = (toolName, checked) =>
  toggleInList(selectedVisionTools, toolName, checked);

const permissionGroups = computed(() => {
  const labels = {
    conversations: t('CUSTOM_ROLE.FORM.PERMISSION_GROUPS.CONVERSATIONS'),
    contactsReports: t('CUSTOM_ROLE.FORM.PERMISSION_GROUPS.CONTACTS_REPORTS'),
    access: t('CUSTOM_ROLE.FORM.PERMISSION_GROUPS.ACCESS'),
    telephony: t('CUSTOM_ROLE.FORM.PERMISSION_GROUPS.TELEPHONY'),
    restrictions: t('CUSTOM_ROLE.FORM.PERMISSION_GROUPS.RESTRICTIONS'),
  };
  const groups = PERMISSION_GROUPS.map(group => ({
    key: group.key,
    label: labels[group.key],
    permissions: group.names.filter(
      permission =>
        AVAILABLE_CUSTOM_ROLE_PERMISSIONS.includes(permission) &&
        isPermissionAvailable(permission)
    ),
  })).filter(group => group.permissions.length);

  // A escondida por recurso fica fora de "Outras" também — senão sairia do
  // grupo dela e reapareceria no balde de não agrupadas.
  const grouped = new Set(groups.flatMap(group => group.permissions));
  const ungrouped = AVAILABLE_CUSTOM_ROLE_PERMISSIONS.filter(
    permission =>
      !grouped.has(permission) &&
      !KANBAN_PERMISSIONS.includes(permission) &&
      isPermissionAvailable(permission)
  );
  if (ungrouped.length) {
    groups.push({
      key: 'other',
      label: t('CUSTOM_ROLE.FORM.PERMISSION_GROUPS.OTHER'),
      permissions: ungrouped,
    });
  }
  return groups;
});

// Catálogo de tools da integração da conta (mesmo endpoint do builder do
// Studio); aqui só interessam as curadas pro Vision.
const fetchVisionTools = async () => {
  visionToolsLoading.value = true;
  visionToolsError.value = false;
  try {
    const { data } = await StudioIntegrationToolsAPI.get();
    visionTools.value = (data?.tools || []).filter(tool => tool.vision_enabled);
  } catch (error) {
    // Falha de rede NÃO pode virar "a conta não tem tools": o admin salvaria
    // uma lista vazia achando que é o estado da conta. Mostra o erro.
    visionToolsError.value = true;
    visionTools.value = [];
  } finally {
    visionToolsLoading.value = false;
  }
};

const visionCatalogReady = computed(
  () => !visionToolsLoading.value && !visionToolsError.value
);

// Agrupa pelo que o admin precisa decidir — o risco da tool —, não pelo
// `group` do catálogo (que é vocabulário do MCP e cai em "Outras" com
// frequência). Write ganha do billing: uma tool que escreve E cobra é
// primeiro uma escrita, e é isso que precisa estar visível.
// O `billing_role` diz o que a tool entrega; o nome dela não. Nenhuma tool se
// chama "boleto", e a 2ª via é justamente o que o admin procura ao montar o
// papel — daí o sufixo. Role sem tradução fica sem sufixo (o nome da tool
// continua na tela): pior seria inventar rótulo pra um papel que não
// conhecemos.
//
// `detalhar_titulo` não tem billing_role no catálogo, mas devolve link do
// boleto, linha digitável e PIX do título informado — o agenteSuporte tirou a
// 2ª via só com ela (conta 10, conv 269, 2026-09-14). Pro admin ela É cobrança.
// simplificação: reconhecida pelo sufixo do nome (contrato `<slug>_<verbo>` do
// registry); upgrade = billing_role próprio no MCP, aí este ramo some.
const billingHintKey = tool => {
  if (tool.billing_role) return tool.billing_role.toUpperCase();
  if (String(tool.name).endsWith('_detalhar_titulo')) return 'DETAIL';
  return '';
};

const billingRoleHint = tool => {
  const role = billingHintKey(tool);
  if (!role) return '';
  const key = `CUSTOM_ROLE.FORM.VISION.BILLING_ROLE.${role}`;
  return te(key) ? t(key) : '';
};

const visionGroups = computed(() => {
  const queries = [];
  const billing = [];
  const writes = [];
  visionTools.value.forEach(tool => {
    if (tool.write_action) writes.push(tool);
    else if (billingHintKey(tool)) billing.push(tool);
    else queries.push(tool);
  });
  return [
    {
      key: 'queries',
      label: t('CUSTOM_ROLE.FORM.VISION.GROUPS.QUERIES'),
      tools: queries,
      write: false,
    },
    {
      key: 'billing',
      label: t('CUSTOM_ROLE.FORM.VISION.GROUPS.BILLING'),
      tools: billing,
      write: false,
    },
    {
      key: 'writes',
      label: t('CUSTOM_ROLE.FORM.VISION.GROUPS.WRITE'),
      tools: writes,
      write: true,
    },
  ].filter(group => group.tools.length);
});

// Tool salva no papel que sumiu do catálogo (integração trocada, tool removida
// do MCP). Continua marcada — some daqui só se o admin desmarcar —, mas
// aparece nomeada pra não virar recorte fantasma.
const unknownSelectedTools = computed(() => {
  if (!visionCatalogReady.value) return [];
  const known = new Set(visionTools.value.map(tool => tool.name));
  return selectedVisionTools.value.filter(toolName => !known.has(toolName));
});

// "Marcar todas" = todas as do CATÁLOGO. Se havia tool órfã marcada, ela sai
// da seleção — e o bloco de órfãs some junto, que é o feedback disso.
const selectAllVisionTools = () => {
  selectedVisionTools.value = visionTools.value.map(tool => tool.name);
};
const clearAllVisionTools = () => {
  selectedVisionTools.value = [];
};

const tabs = computed(() => {
  const visionLabel = t('CUSTOM_ROLE.FORM.VISION.LABEL');
  const hasCatalog = visionCatalogReady.value && visionTools.value.length > 0;
  const all = [
    {
      key: PERMISSIONS_TAB,
      label: t('CUSTOM_ROLE.FORM.PERMISSIONS.LABEL'),
      // Conta só o que está na tela: permissão salva de recurso desligado não
      // tem toggle visível, e um "1" sem nada ligado não se explica.
      count: selectedPermissions.value.filter(
        permission =>
          !KANBAN_PERMISSIONS.includes(permission) &&
          isPermissionAvailable(permission)
      ).length,
    },
    {
      key: KANBAN_TAB,
      label: t('CUSTOM_ROLE.FORM.KANBAN.LABEL'),
      count: selectedPermissions.value.filter(permission =>
        KANBAN_PERMISSIONS.includes(permission)
      ).length,
    },
    {
      key: VISION_TAB,
      // O contador vai no RÓTULO (3/18) porque o que importa aqui é a razão,
      // não o total: "3" sozinho não diz se sobrou tool de fora.
      label: hasCatalog
        ? `${visionLabel} · ${selectedVisionTools.value.length}/${visionTools.value.length}`
        : visionLabel,
      // Catálogo que não carregou fica atrás da aba fechada; o ícone é o que
      // trai o erro pra quem está na aba de permissões.
      ...(visionToolsError.value ? { icon: 'i-lucide-triangle-alert' } : {}),
    },
  ];
  return all.filter(
    tab =>
      (tab.key !== KANBAN_TAB || isKanbanEnabled.value) &&
      (tab.key !== VISION_TAB || isVisionEnabled.value)
  );
});

onMounted(() => {
  if (props.mode === 'edit') {
    populateEditForm();
  }
  // Sem o recurso a aba não existe: buscar o catálogo só geraria o ícone de
  // erro numa aba que ninguém vê (ou uma chamada à toa).
  if (isVisionEnabled.value) fetchVisionTools();
  // Focus the name input when mounted
  nameInput.value?.focus();
});

const getTranslationKey = base => {
  return props.mode === 'edit'
    ? `CUSTOM_ROLE.EDIT.${base}`
    : `CUSTOM_ROLE.ADD.${base}`;
};

const modalTitle = computed(() => t(getTranslationKey('TITLE')));
const modalDescription = computed(() => t(getTranslationKey('DESC')));
const submitButtonText = computed(() => t(getTranslationKey('SUBMIT')));

// Com abas, o campo que falta pode estar na aba fechada — botão desabilitado
// sem dizer por quê vira beco sem saída. O rodapé nomeia o que falta.
const missingFields = computed(() => {
  const missing = [];
  if (v$.value.name.$invalid) missing.push(t('CUSTOM_ROLE.FORM.NAME.LABEL'));
  if (v$.value.description.$invalid)
    missing.push(t('CUSTOM_ROLE.FORM.DESCRIPTION.LABEL'));
  if (v$.value.selectedPermissions.$invalid)
    missing.push(t('CUSTOM_ROLE.FORM.PERMISSIONS.LABEL'));
  return missing;
});

const validationHint = computed(() => {
  if (!v$.value.$anyDirty || !missingFields.value.length) return '';
  return t('CUSTOM_ROLE.FORM.MISSING_FIELDS', {
    fields: missingFields.value.join(', '),
  });
});

// Papel só com tools do Vision e nenhuma permissão: é o caso mais comum de
// "não deixa salvar" — o usuário está na aba Vision e a exigência (regra do
// Chatwoot: papel sem permissão deixa o agente sem acesso às conversas) está na
// aba fechada. O rodapé explica e leva pra lá.
const permissionsMissing = computed(
  () =>
    v$.value.$anyDirty &&
    !v$.value.name.$invalid &&
    !v$.value.description.$invalid &&
    v$.value.selectedPermissions.$invalid
);
const goToPermissions = () => {
  activeTab.value = PERMISSIONS_TAB;
  v$.value.selectedPermissions.$touch();
};

const handleCustomRole = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  addCustomRole.showLoading = true;
  try {
    const roleData = {
      name: name.value,
      description: description.value,
      permissions: selectedPermissions.value,
      // Sempre lista (possivelmente vazia): lista vazia é o Vision desligado
      // pro papel, nunca "sem restrição".
      vision_tools: selectedVisionTools.value,
    };

    if (props.mode === 'edit') {
      await store.dispatch('customRole/updateCustomRole', {
        id: props.selectedRole.id,
        ...roleData,
      });
      toast.success(t('CUSTOM_ROLE.EDIT.API.SUCCESS_MESSAGE'));
    } else {
      await store.dispatch('customRole/createCustomRole', roleData);
      toast.success(t('CUSTOM_ROLE.ADD.API.SUCCESS_MESSAGE'));
    }

    resetForm();
    emit('close');
  } catch (error) {
    const errorMessage =
      error?.message || t(`CUSTOM_ROLE.FORM.API.ERROR_MESSAGE`);
    toast.error(errorMessage);
  } finally {
    addCustomRole.showLoading = false;
  }
};

const isSubmitDisabled = computed(
  () => v$.value.$invalid || addCustomRole.showLoading
);
</script>

<template>
  <!-- max-h + min-h-0 na cadeia: quem rola é o painel da aba, então o rodapé
       fica visível mesmo em tela baixa (o modal não estoura a viewport). -->
  <div class="flex flex-col max-h-[90vh]">
    <woot-modal-header
      :header-title="modalTitle"
      :header-content="modalDescription"
    />
    <form
      class="flex flex-col w-full min-h-0"
      @submit.prevent="handleCustomRole"
    >
      <div class="grid w-full grid-cols-1 gap-x-6 sm:grid-cols-2">
        <label :class="{ error: v$.name.$error }">
          {{ $t('CUSTOM_ROLE.FORM.NAME.LABEL') }}
          <input
            ref="nameInput"
            v-model.trim="name"
            type="text"
            class="!mb-0"
            :placeholder="$t('CUSTOM_ROLE.FORM.NAME.PLACEHOLDER')"
            @blur="v$.name.$touch"
          />
        </label>

        <label :class="{ error: v$.description.$error }">
          {{ $t('CUSTOM_ROLE.FORM.DESCRIPTION.LABEL') }}
          <textarea
            v-model="description"
            :rows="2"
            class="!mb-0 resize-none"
            :placeholder="$t('CUSTOM_ROLE.FORM.DESCRIPTION.PLACEHOLDER')"
            @blur="v$.description.$touch"
          />
        </label>
      </div>

      <!-- Uma aba só (conta sem Kanban e sem Vision) não é navegação: some. -->
      <UnderlineTabs
        v-if="tabs.length > 1"
        v-model="activeTab"
        :tabs="tabs"
        class="flex-shrink-0 px-8 mt-4 -mx-8"
      />

      <!-- pr-4 + scrollbar-gutter: a barra de rolagem ganha faixa própria e
           para de encostar na 2ª coluna; pb-6 impede a última linha de colar no
           rodapé. -->
      <div
        role="tabpanel"
        class="min-h-0 max-h-[60vh] overflow-y-auto pt-4 pb-6 -ml-1 pl-1 pr-4 [scrollbar-gutter:stable]"
      >
        <!-- ── Permissões ────────────────────────────────────────────────── -->
        <div v-if="activeTab === PERMISSIONS_TAB" class="flex flex-col gap-4">
          <p
            v-if="v$.selectedPermissions.$error"
            class="text-sm text-n-ruby-11 rounded-md bg-n-ruby-3 px-3 py-2"
          >
            {{ $t('CUSTOM_ROLE.FORM.PERMISSIONS.ERROR') }}
          </p>
          <div
            v-for="group in permissionGroups"
            :key="group.key"
            class="flex flex-col gap-2"
          >
            <span
              class="text-xs font-semibold tracking-wide uppercase text-n-slate-10"
            >
              {{ group.label }}
            </span>
            <div class="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
              <div
                v-for="permission in group.permissions"
                :key="permission"
                class="flex items-start justify-between gap-3"
              >
                <label
                  :id="`permission-label-${permission}`"
                  :for="permission"
                  class="min-w-0 text-sm font-normal leading-5 cursor-pointer"
                >
                  {{
                    $t(`CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`)
                  }}
                </label>
                <Switch
                  :id="permission"
                  :aria-labelledby="`permission-label-${permission}`"
                  :data-permission="permission"
                  class="mt-0.5"
                  :model-value="selectedPermissions.includes(permission)"
                  @update:model-value="
                    checked => togglePermission(permission, checked)
                  "
                />
              </div>
            </div>
          </div>
        </div>

        <!-- ── Kanban ────────────────────────────────────────────────────── -->
        <KanbanRolePermissions
          v-else-if="activeTab === KANBAN_TAB"
          v-model="selectedPermissions"
        />

        <!-- ── Assistente Vision ─────────────────────────────────────────── -->
        <div v-else-if="activeTab === VISION_TAB" class="flex flex-col gap-3">
          <p class="text-sm text-n-slate-11">
            {{ $t('CUSTOM_ROLE.FORM.VISION.DESCRIPTION') }}
          </p>

          <p v-if="visionToolsLoading" class="text-sm text-n-slate-11">
            {{ $t('CUSTOM_ROLE.FORM.VISION.LOADING') }}
          </p>

          <div
            v-else-if="visionToolsError"
            class="flex items-center justify-between gap-2 p-3 text-sm rounded-lg bg-n-ruby-3 text-n-ruby-11"
          >
            <span>{{ $t('CUSTOM_ROLE.FORM.VISION.ERROR') }}</span>
            <Button
              xs
              faded
              ruby
              type="button"
              :label="$t('CUSTOM_ROLE.FORM.VISION.RETRY')"
              @click="fetchVisionTools"
            />
          </div>

          <div
            v-else-if="!visionTools.length"
            class="flex flex-col gap-1 p-3 text-sm rounded-lg bg-n-amber-3 text-n-amber-11"
          >
            <span>{{ $t('CUSTOM_ROLE.FORM.VISION.EMPTY') }}</span>
            <span v-if="!selectedVisionTools.length" class="text-xs">
              {{ $t('CUSTOM_ROLE.FORM.VISION.EMPTY_HINT') }}
            </span>
          </div>

          <template v-else>
            <div class="flex items-center justify-between gap-2">
              <span class="text-xs tabular-nums text-n-slate-11">
                {{
                  t('CUSTOM_ROLE.FORM.VISION.SELECTED_COUNT', {
                    selected: selectedVisionTools.length,
                    total: visionTools.length,
                  })
                }}
              </span>
              <div class="flex gap-1">
                <Button
                  xs
                  faded
                  slate
                  type="button"
                  :label="$t('CUSTOM_ROLE.FORM.VISION.SELECT_ALL')"
                  @click="selectAllVisionTools"
                />
                <Button
                  xs
                  faded
                  slate
                  type="button"
                  :label="$t('CUSTOM_ROLE.FORM.VISION.CLEAR_ALL')"
                  @click="clearAllVisionTools"
                />
              </div>
            </div>

            <div
              v-if="!selectedVisionTools.length"
              class="p-3 text-sm rounded-lg bg-n-amber-3 text-n-amber-11"
            >
              {{ $t('CUSTOM_ROLE.FORM.VISION.NONE_WARNING') }}
            </div>

            <div
              v-for="group in visionGroups"
              :key="group.key"
              :data-vision-group="group.key"
              class="flex flex-col gap-2"
            >
              <span
                class="flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase"
                :class="group.write ? 'text-n-amber-11' : 'text-n-slate-10'"
              >
                <span
                  v-if="group.write"
                  v-tooltip.top="$t('CUSTOM_ROLE.FORM.VISION.WRITE_ACTION')"
                  class="i-lucide-pencil size-3.5"
                />
                {{ group.label }}
              </span>
              <div class="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
                <div
                  v-for="tool in group.tools"
                  :key="tool.name"
                  class="flex items-start justify-between gap-3"
                >
                  <label
                    :id="`vision-tool-label-${tool.name}`"
                    v-tooltip.top="tool.description || ''"
                    :for="`vision-tool-${tool.name}`"
                    class="min-w-0 text-sm font-normal leading-5 cursor-pointer text-n-slate-12"
                  >
                    {{ tool.display_label || tool.name }}
                    <span v-if="billingRoleHint(tool)" class="text-n-slate-11">
                      {{ billingRoleHint(tool) }}
                    </span>
                  </label>
                  <Switch
                    :id="`vision-tool-${tool.name}`"
                    :aria-labelledby="`vision-tool-label-${tool.name}`"
                    :data-vision-tool="tool.name"
                    class="mt-0.5"
                    :model-value="selectedVisionTools.includes(tool.name)"
                    @update:model-value="
                      checked => toggleVisionTool(tool.name, checked)
                    "
                  />
                </div>
              </div>
            </div>
          </template>

          <div
            v-if="unknownSelectedTools.length"
            class="flex flex-col gap-2 p-3 rounded-lg bg-n-slate-3"
          >
            <span class="text-xs font-semibold text-n-slate-12">
              {{ $t('CUSTOM_ROLE.FORM.VISION.UNKNOWN_TITLE') }}
            </span>
            <div class="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
              <div
                v-for="toolName in unknownSelectedTools"
                :key="toolName"
                class="flex items-start justify-between gap-3"
              >
                <label
                  :id="`vision-tool-unknown-label-${toolName}`"
                  :for="`vision-tool-unknown-${toolName}`"
                  class="min-w-0 text-sm font-normal leading-5 cursor-pointer text-n-slate-11"
                >
                  {{ toolName }}
                </label>
                <Switch
                  :id="`vision-tool-unknown-${toolName}`"
                  :aria-labelledby="`vision-tool-unknown-label-${toolName}`"
                  :data-vision-tool="toolName"
                  class="mt-0.5"
                  :model-value="selectedVisionTools.includes(toolName)"
                  @update:model-value="
                    checked => toggleVisionTool(toolName, checked)
                  "
                />
              </div>
            </div>
            <span class="text-xs text-n-slate-11">
              {{ $t('CUSTOM_ROLE.FORM.VISION.UNKNOWN_HINT') }}
            </span>
          </div>
        </div>
      </div>

      <div
        class="flex flex-row items-center justify-end flex-shrink-0 gap-2 px-8 pt-4 mt-2 -mx-8 border-t border-n-weak"
      >
        <button
          v-if="permissionsMissing"
          type="button"
          class="text-xs ltr:mr-auto rtl:ml-auto text-n-ruby-11 text-left underline underline-offset-2 hover:text-n-ruby-12"
          @click="goToPermissions"
        >
          {{ $t('CUSTOM_ROLE.FORM.PERMISSIONS.MISSING_HINT') }}
        </button>
        <span
          v-else-if="validationHint"
          class="text-xs ltr:mr-auto rtl:ml-auto text-n-ruby-11"
        >
          {{ validationHint }}
        </span>
        <Button
          faded
          slate
          type="reset"
          :label="$t('CUSTOM_ROLE.FORM.CANCEL_BUTTON_TEXT')"
          @click.prevent="emit('close')"
        />
        <Button
          type="submit"
          :label="submitButtonText"
          :disabled="isSubmitDisabled"
          :is-loading="addCustomRole.showLoading"
        />
      </div>
    </form>
  </div>
</template>
